/**
 * PHRASE BLOOM のシンセ音声グラフ。
 *
 * `BaseAudioContext` を受けて動くため、リアルタイム再生（AudioContext）と
 * WAV 書き出し（OfflineAudioContext）で完全に同じコードを共用する。
 * 「モニターで聴いた音がそのまま WAV になる」ことをここで保証する。
 * Kaitlyn の揺らぎも Math.random ではなくノート由来のシードから導出し、
 * この保証を守る。
 */

import { mulberry32 } from "./phraseGenerator";

export type OscType = "sine" | "triangle" | "sawtooth" | "square" | "noise";

export interface DelayDivision {
  id: string;
  label: string;
  /** 四分音符 = 1 とした拍数 */
  beats: number;
}

export const DELAY_DIVISIONS: DelayDivision[] = [
  { id: "1/32", label: "1/32", beats: 0.125 },
  { id: "1/16", label: "1/16", beats: 0.25 },
  { id: "1/16.", label: "1/16.", beats: 0.375 },
  { id: "1/8", label: "1/8", beats: 0.5 },
  { id: "1/8.", label: "1/8.", beats: 0.75 },
  { id: "1/4", label: "1/4", beats: 1 },
  { id: "1/4.", label: "1/4.", beats: 1.5 },
  { id: "1/2", label: "1/2", beats: 2 },
  { id: "1/1", label: "1/1", beats: 4 },
];

/** DelayNode の確保長（秒）。1/1 @ 60bpm = 4 秒 + 余裕 */
export const MAX_DELAY_SEC = 5;
export const DELAY_MS_MIN = 1;
export const DELAY_MS_MAX = 4000;

export type DelayMode = "sync" | "ms";

export interface SynthParams {
  /** メインフレーズのオシレーター */
  osc: OscType;
  /**
   * GOLD モード。矩形波（SQR）のボイスに矩形波 LFO でオクターブを
   * かけ、コイン/ゴールド系のきらめきを出す（SQR 以外のボイスには無効）
   */
  goldOn: boolean;
  /** GOLD のオクターブ LFO レート Hz（GOLD_RATE_MIN..GOLD_RATE_MAX） */
  goldRateHz: number;
  /** SOUND A（ユニゾン/ハモリ）のオシレーター */
  soundAOsc: OscType;
  /** SOUND B（ツイン）のオシレーター */
  soundBOsc: OscType;
  /** グライドのピッチ遷移時間 ms（10..300） */
  glideMs: number;
  /** ディレイタイムの指定方法（テンポ同期 / ms 直接入力） */
  delayMode: DelayMode;
  /** DELAY_DIVISIONS の id（sync モード時） */
  delayDivisionId: string;
  /** ディレイタイム ms（ms モード時、1..4000） */
  delayMs: number;
  /** 0..0.9 */
  delayFeedback: number;
  /** 0..1（wet の割合） */
  delayMix: number;
  /** クリッパー ON/OFF */
  clipOn: boolean;
  /** クリッパーの入力ドライブ 0..24 (dB) */
  clipDrive: number;
  /**
   * Kaitlyn モード。ピッチ・音量・アタックへノートごとの決定論的な
   * 微細揺らぎを加え、機械的な均一さをほどく（かすかな有機的揺らぎ）
   */
  kaitlynOn: boolean;
}

export const DEFAULT_SYNTH_PARAMS: SynthParams = {
  osc: "square",
  goldOn: false,
  goldRateHz: 8,
  soundAOsc: "sine",
  soundBOsc: "triangle",
  glideMs: 90,
  delayMode: "sync",
  delayDivisionId: "1/8",
  delayMs: 350,
  delayFeedback: 0.35,
  delayMix: 0.25,
  clipOn: false,
  clipDrive: 6,
  kaitlynOn: false,
};

/** GOLD のオクターブ LFO レート範囲（Hz）。低速=トリル、高速=きらめき */
export const GOLD_RATE_MIN = 6;
export const GOLD_RATE_MAX = 22;

export const GLIDE_MS_MIN = 10;
export const GLIDE_MS_MAX = 300;

export const midiToFreq = (midi: number): number =>
  440 * Math.pow(2, (midi - 69) / 12);

/** ディレイタイム（秒）。sync はテンポ同期、ms は直接指定 */
export const delayTimeSeconds = (params: SynthParams, bpm: number): number => {
  if (params.delayMode === "ms") {
    const ms = Math.min(DELAY_MS_MAX, Math.max(DELAY_MS_MIN, params.delayMs));
    return ms / 1000;
  }
  const div =
    DELAY_DIVISIONS.find((d) => d.id === params.delayDivisionId) ??
    DELAY_DIVISIONS[3];
  return Math.min(MAX_DELAY_SEC, div.beats * (60 / bpm));
};

/** ノートのリリース長（秒）。停止スケジュールとテール計算で共用する */
export const NOTE_RELEASE_SEC = 0.08;

/** コンテキストごとにノイズバッファを使い回す */
const noiseBufferCache = new WeakMap<BaseAudioContext, AudioBuffer>();

const getNoiseBuffer = (ctx: BaseAudioContext): AudioBuffer => {
  const cached = noiseBufferCache.get(ctx);
  if (cached) return cached;
  const length = Math.ceil(ctx.sampleRate * 2);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  noiseBufferCache.set(ctx, buffer);
  return buffer;
};

/** tanh ソフトクリップのカーブ（drive dB で入力を押し込む。出力は ±1 に収束） */
const makeClipperCurve = (driveDb: number): Float32Array<ArrayBuffer> => {
  const n = 2048;
  const drive = Math.pow(10, driveDb / 20);
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * drive);
  }
  return curve;
};

export interface SynthChain {
  /** ボイスの接続先 */
  input: GainNode;
  dispose: () => void;
}

/**
 * マスターチェーンを構築する。
 *
 *   input ─┬─ dry ────────────────┬─ compressor ─ (clipper) ─ destination
 *          └─ delay ─ wet ────────┘
 *             ↑  └ lowpass ─ feedback ┐
 *             └───────────────────────┘
 */
export const createSynthChain = (
  ctx: BaseAudioContext,
  destination: AudioNode,
  params: SynthParams,
  bpm: number,
): SynthChain => {
  const input = ctx.createGain();
  input.gain.value = 1;

  // 重なったノートやディレイの蓄積でクリップしないよう軽く潰す
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -12;
  compressor.knee.value = 24;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.25;

  // クリッパー（ON 時のみ挿入）。tanh で天井を作り倍音を足す
  let clipper: WaveShaperNode | null = null;
  if (params.clipOn) {
    clipper = ctx.createWaveShaper();
    clipper.curve = makeClipperCurve(params.clipDrive);
    clipper.oversample = "4x";
    compressor.connect(clipper);
    clipper.connect(destination);
  } else {
    compressor.connect(destination);
  }

  const dry = ctx.createGain();
  dry.gain.value = 1 - params.delayMix * 0.5; // wet 100% でも原音を半分残す
  input.connect(dry);
  dry.connect(compressor);

  const delay = ctx.createDelay(MAX_DELAY_SEC);
  delay.delayTime.value = delayTimeSeconds(params, bpm);

  const feedback = ctx.createGain();
  feedback.gain.value = params.delayFeedback;

  // リピートするほど丸くなる、アナログ風のフィードバックパス
  const damp = ctx.createBiquadFilter();
  damp.type = "lowpass";
  damp.frequency.value = 4000;

  const wet = ctx.createGain();
  wet.gain.value = params.delayMix;

  input.connect(delay);
  delay.connect(wet);
  wet.connect(compressor);
  delay.connect(damp);
  damp.connect(feedback);
  feedback.connect(delay);

  return {
    input,
    dispose: () => {
      input.disconnect();
      dry.disconnect();
      delay.disconnect();
      damp.disconnect();
      feedback.disconnect();
      wet.disconnect();
      compressor.disconnect();
      clipper?.disconnect();
    },
  };
};

// ---- Kaitlyn（かすかな有機的揺らぎ） ----

/** 音量揺らぎの深さ（±4%） */
const KAITLYN_TREMOLO_DEPTH = 0.04;

/** ノートシードから決定論的に導出する揺らぎ量のセット */
interface KaitlynWobble {
  /** アタック時間（秒、4〜9ms のばらつき） */
  attackSec: number;
  /** 音ごとの固定チューニングずれ（セント、±4） */
  staticDetuneCents: number;
  /** ピッチ LFO のレート（Hz、0.7〜1.5） */
  pitchLfoRateHz: number;
  /** ピッチ LFO の深さ（セント、3〜6。符号で揺れの立ち上がり方向もばらす） */
  pitchLfoDepthCents: number;
  /** 音量揺らぎのレート（Hz、2.5〜4.5） */
  tremoloRateHz: number;
  /** 音量のわずかな個体差（0.97〜1.0） */
  levelScale: number;
}

/**
 * wobbleSeed から揺らぎ量を導出する。Math.random を使わないため、
 * リアルタイム再生と WAV 書き出しで完全に同じ揺らぎになる。
 */
const deriveKaitlynWobble = (seed: number): KaitlynWobble => {
  const rand = mulberry32(seed);
  const lfoSign = rand() < 0.5 ? -1 : 1;
  return {
    attackSec: 0.004 + rand() * 0.005,
    staticDetuneCents: (rand() * 2 - 1) * 4,
    pitchLfoRateHz: 0.7 + rand() * 0.8,
    pitchLfoDepthCents: lfoSign * (3 + rand() * 3),
    tremoloRateHz: 2.5 + rand() * 2,
    levelScale: 1 - rand() * 0.03,
  };
};

/**
 * アレンジメントイベント → Kaitlyn 用シード。
 * リアルタイム再生（useSynthEngine）と WAV 書き出し（wavEncoder）の両方が
 * この式を共有することで、同一ノートに同一の揺らぎを保証する。
 */
export const wobbleSeedFor = (
  layer: "main" | "soundA" | "soundB",
  note: { tick: number; midiNote: number },
): number => {
  const layerOffset = layer === "main" ? 0 : layer === "soundA" ? 101 : 211;
  return (note.tick * 31 + note.midiNote * 7 + layerOffset) >>> 0;
};

/** 1 ボイス分のスケジュール指定 */
export interface VoiceOptions {
  osc: OscType;
  midiNote: number;
  velocity: number;
  startTime: number;
  durationSec: number;
  /** グライド先の MIDI ノート（音の末尾でこのピッチへ遷移） */
  glideToMidi?: number;
  /** グライドのピッチ遷移時間（秒） */
  glideSec: number;
  /** GOLD モード（osc が square のときだけオクターブ LFO をかける） */
  gold: boolean;
  /** GOLD のオクターブ LFO レート（Hz） */
  goldRateHz: number;
  /** Kaitlyn モード（かすかな有機的揺らぎ） */
  kaitlyn: boolean;
  /** Kaitlyn の決定論シード（wobbleSeedFor で導出する） */
  wobbleSeed: number;
}

/**
 * 1 ノートをスケジュールする。
 * glideToMidi を渡すと、音の末尾（次の音の開始に一致する durationSec の
 * 終端）へ向けてピッチをなめらかに遷移させる。
 * 戻り値はソースノード（stop() 済みのため通常は放置でよい）。
 */
export const scheduleVoice = (
  ctx: BaseAudioContext,
  chainInput: GainNode,
  voice: VoiceOptions,
): AudioScheduledSourceNode => {
  const { osc, midiNote, velocity, startTime, durationSec, glideToMidi } = voice;
  const wobble = voice.kaitlyn ? deriveKaitlynWobble(voice.wobbleSeed) : null;
  // 固定デチューンは基準周波数へ乗せる（オシレーターとノイズ BPF の両方に効く）
  const detuneRatio = wobble ? Math.pow(2, wobble.staticDetuneCents / 1200) : 1;
  const freq = midiToFreq(midiNote) * detuneRatio;
  const level =
    Math.pow(velocity / 127, 1.6) *
    (osc === "noise" ? 0.5 : 0.28) *
    (wobble ? wobble.levelScale : 1);

  const env = ctx.createGain();
  const attack = wobble ? wobble.attackSec : 0.005;
  const stopAt = startTime + durationSec + NOTE_RELEASE_SEC;
  env.gain.setValueAtTime(0, startTime);
  env.gain.linearRampToValueAtTime(level, startTime + attack);
  env.gain.setValueAtTime(level, Math.max(startTime + attack, startTime + durationSec));
  env.gain.linearRampToValueAtTime(0, stopAt);

  // Kaitlyn: 音量のごく浅い揺らぎ。env 後段の乗算ゲイン（基準 1.0）へ
  // 遅いサイン LFO を ±KAITLYN_TREMOLO_DEPTH で注入する
  let tremoloLfo: OscillatorNode | null = null;
  let tremoloDepth: GainNode | null = null;
  let wobbleGain: GainNode | null = null;
  if (wobble) {
    wobbleGain = ctx.createGain();
    wobbleGain.gain.value = 1;
    tremoloLfo = ctx.createOscillator();
    tremoloLfo.type = "sine";
    tremoloLfo.frequency.setValueAtTime(wobble.tremoloRateHz, startTime);
    tremoloDepth = ctx.createGain();
    tremoloDepth.gain.value = KAITLYN_TREMOLO_DEPTH;
    tremoloLfo.connect(tremoloDepth);
    tremoloDepth.connect(wobbleGain.gain);
    tremoloLfo.start(startTime);
    tremoloLfo.stop(stopAt + 0.01);
    env.connect(wobbleGain);
    wobbleGain.connect(chainInput);
  } else {
    env.connect(chainInput);
  }

  // Kaitlyn: 遅いサイン LFO で音程をかすかに揺らす。
  // centsToUnit はターゲットパラメータの単位変換（detune はそのままセント、
  // ノイズ BPF の frequency は Hz へ換算）
  let pitchLfo: OscillatorNode | null = null;
  let pitchLfoDepth: GainNode | null = null;
  const applyKaitlynPitchLfo = (param: AudioParam, centsToUnit: number) => {
    if (!wobble) return;
    pitchLfo = ctx.createOscillator();
    pitchLfo.type = "sine";
    pitchLfo.frequency.setValueAtTime(wobble.pitchLfoRateHz, startTime);
    pitchLfoDepth = ctx.createGain();
    pitchLfoDepth.gain.value = wobble.pitchLfoDepthCents * centsToUnit;
    pitchLfo.connect(pitchLfoDepth);
    pitchLfoDepth.connect(param);
    pitchLfo.start(startTime);
    pitchLfo.stop(stopAt + 0.01);
  };

  // 音の末尾 glideSec（短い音なら後半 1/2）でターゲットピッチへ滑る
  const applyGlide = (freqParam: AudioParam, maxFreq: number) => {
    if (glideToMidi === undefined) return;
    const target = Math.min(midiToFreq(glideToMidi), maxFreq);
    const glideBegin =
      startTime + Math.max(durationSec - voice.glideSec, durationSec * 0.5);
    freqParam.setValueAtTime(Math.min(freq, maxFreq), glideBegin);
    freqParam.exponentialRampToValueAtTime(target, startTime + durationSec);
  };

  let source: AudioScheduledSourceNode;
  let goldLfo: OscillatorNode | null = null;
  let goldDepth: GainNode | null = null;

  if (osc === "noise") {
    const noise = ctx.createBufferSource();
    noise.buffer = getNoiseBuffer(ctx);
    noise.loop = true;
    // バンドパスをノート周波数に追従させ、ノイズにも音程感を与える
    const nyquistCap = ctx.sampleRate / 2 - 100;
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.setValueAtTime(Math.min(freq, nyquistCap), startTime);
    bandpass.Q.value = 8;
    applyGlide(bandpass.frequency, nyquistCap);
    // セント → Hz 換算（1 セント ≈ freq * ln2 / 1200）で BPF を揺らす
    applyKaitlynPitchLfo(bandpass.frequency, (freq * Math.LN2) / 1200);
    noise.connect(bandpass);
    bandpass.connect(env);
    source = noise;
  } else {
    const oscNode = ctx.createOscillator();
    oscNode.type = osc;
    oscNode.frequency.setValueAtTime(freq, startTime);
    applyGlide(oscNode.frequency, 20000);
    applyKaitlynPitchLfo(oscNode.detune, 1);

    // GOLD: 矩形波 LFO（±1）を detune へ ±600 セントで注入し、
    // 基音とオクターブ上（0 / +1200 セント）を行き来させる
    if (voice.gold && osc === "square") {
      oscNode.detune.setValueAtTime(600, startTime);
      goldLfo = ctx.createOscillator();
      goldLfo.type = "square";
      goldLfo.frequency.setValueAtTime(voice.goldRateHz, startTime);
      goldDepth = ctx.createGain();
      goldDepth.gain.value = 600;
      goldLfo.connect(goldDepth);
      goldDepth.connect(oscNode.detune);
      goldLfo.start(startTime);
      goldLfo.stop(stopAt + 0.01);
    }

    oscNode.connect(env);
    source = oscNode;
  }

  source.start(startTime);
  source.stop(stopAt + 0.01);
  source.onended = () => {
    source.disconnect();
    env.disconnect();
    goldLfo?.disconnect();
    goldDepth?.disconnect();
    pitchLfo?.disconnect();
    pitchLfoDepth?.disconnect();
    tremoloLfo?.disconnect();
    tremoloDepth?.disconnect();
    wobbleGain?.disconnect();
  };
  return source;
};

/**
 * ディレイの残響テール長（秒）を見積もる。
 * フィードバック率が -60dB まで減衰する回数から算出し、上限 8 秒。
 */
export const estimateDelayTail = (params: SynthParams, bpm: number): number => {
  if (params.delayMix <= 0.001) return 0.1;
  const fb = Math.min(0.9, Math.max(0.001, params.delayFeedback));
  const time = delayTimeSeconds(params, bpm);
  const repeats = fb <= 0.01 ? 1 : Math.ceil(Math.log(0.001) / Math.log(fb));
  return Math.min(8, time * (repeats + 1));
};
